import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';

export type DirecaoOrdenacao = 'asc' | 'desc';

export interface Ordenacao {
  /**
   * `TAB-01`/`CTR-12` — **nome do campo na API**, nunca o rótulo da coluna. Traduzir rótulo
   * para ordenar exige um tradutor que quebra calado quando o contrato ganha um campo.
   */
  campo: string;
  direcao: DirecaoOrdenacao;
}

/**
 * Cabeçalho de coluna ordenável (`TAB-01`), num lugar só.
 *
 * Usado como atributo do próprio `<th>`, para a célula continuar sendo cabeçalho de tabela
 * e o `aria-sort` ficar onde leitor de tela espera encontrá-lo.
 */
@Component({
  selector: 'th[appOrdenavel]',
  standalone: true,
  imports: [CommonModule],
  template: `
    <button type="button" class="ord" (click)="alternar()" [attr.aria-label]="'Ordenar por ' + rotuloAcessivel">
      <ng-content></ng-content>
      <span class="ord-seta" aria-hidden="true" [class.ord-seta--ativa]="ativa">{{ seta }}</span>
    </button>
  `,
  styles: [
    `
      .ord {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        padding: 0;
        border: 0;
        background: none;
        font: inherit;
        color: inherit;
        cursor: pointer;
        text-align: left;
      }

      .ord-seta {
        font-size: 0.75em;
        opacity: 0.35;
      }

      .ord-seta--ativa {
        opacity: 1;
      }
    `,
  ],
  host: { '[attr.aria-sort]': 'ariaSort' },
})
export class OrdenavelComponent {
  /** Nome do campo na API. */
  @Input({ required: true }) campo = '';

  /** Ordenação corrente da tabela inteira; nula quando nada está ordenado. */
  @Input() ordenacao: Ordenacao | null = null;

  /** Só para o `aria-label`, já que o rótulo visível vem por projeção de conteúdo. */
  @Input() rotuloAcessivel = '';

  @Output() ordenar = new EventEmitter<Ordenacao>();

  get ativa(): boolean {
    return this.ordenacao?.campo === this.campo;
  }

  get seta(): string {
    if (!this.ativa) return '↕';
    return this.ordenacao?.direcao === 'asc' ? '↑' : '↓';
  }

  /** `null` fora da coluna ativa: leitor de tela não anuncia ordem onde não há. */
  get ariaSort(): string | null {
    if (!this.ativa) return null;
    return this.ordenacao?.direcao === 'asc' ? 'ascending' : 'descending';
  }

  /** Primeiro clique ordena crescente; no mesmo campo, inverte. */
  alternar(): void {
    const direcao: DirecaoOrdenacao = this.ativa && this.ordenacao?.direcao === 'asc' ? 'desc' : 'asc';
    this.ordenar.emit({ campo: this.campo, direcao });
  }
}

/** Formato do Spring Data aceito pelo contrato: `campo,asc|desc`. */
export function paraParametroSort(o: Ordenacao | null): string | null {
  return o ? `${o.campo},${o.direcao}` : null;
}
